import { useState, useEffect, useRef } from "react";

const theme = {
  bg: "#0d0305",
  bgCard: "#1a0508",
  bgInput: "#120306",
  primary: "#c0152a",
  primaryLight: "#e8192f",
  primaryDark: "#8b0e1e",
  accent: "#ff4d5e",
  accentGold: "#f5c518",
  text: "#f5e6e8",
  textMuted: "#9a7a80",
  border: "#3d1218",
  success: "#2ecc71",
  danger: "#e74c3c",
  gradHigh: "linear-gradient(135deg, #c0152a 0%, #8b0e1e 100%)",
  gradLow: "linear-gradient(135deg, #1a0508 0%, #3d1218 100%)",
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Rajdhani:wght@400;500;600;700&family=Orbitron:wght@400;700;900&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }

  body { background: ${theme.bg}; color: ${theme.text}; font-family: 'Rajdhani', sans-serif; }

  .app { min-height: 100vh; background: ${theme.bg}; position: relative; overflow-x: hidden; }
  .app::before {
    content: '';
    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
    background: radial-gradient(ellipse at 20% 50%, rgba(192,21,42,0.08) 0%, transparent 60%),
                radial-gradient(ellipse at 80% 20%, rgba(139,14,30,0.06) 0%, transparent 50%);
    pointer-events: none; z-index: 0;
  }

  .page { position: relative; z-index: 1; min-height: 100vh; display: flex; flex-direction: column; }

  /* AUTH PAGES */
  .auth-page {
    min-height: 100vh;
    display: flex; align-items: center; justify-content: center;
    padding: 20px;
    background: radial-gradient(ellipse at center, #1a0508 0%, #0d0305 70%);
  }
  .auth-card {
    background: ${theme.bgCard};
    border: 1px solid ${theme.border};
    border-radius: 16px;
    padding: 40px 36px;
    width: 100%; max-width: 420px;
    box-shadow: 0 0 60px rgba(192,21,42,0.15), inset 0 1px 0 rgba(255,255,255,0.05);
    animation: slideUp 0.5s ease;
  }
  @keyframes slideUp {
    from { opacity: 0; transform: translateY(30px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .auth-logo {
    text-align: center; margin-bottom: 32px;
  }
  .auth-logo .logo-icon {
    width: 64px; height: 64px;
    background: ${theme.gradHigh};
    border-radius: 16px;
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 12px;
    font-size: 28px;
    box-shadow: 0 8px 32px rgba(192,21,42,0.4);
    animation: pulse 2s infinite;
  }
  @keyframes pulse {
    0%,100% { box-shadow: 0 8px 32px rgba(192,21,42,0.4); }
    50%      { box-shadow: 0 8px 48px rgba(192,21,42,0.7); }
  }
  .auth-logo h1 {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 32px; letter-spacing: 3px; color: ${theme.text};
  }
  .auth-logo p { color: ${theme.textMuted}; font-size: 13px; margin-top: 4px; }

  .form-group { margin-bottom: 20px; }
  .form-label { display: block; font-size: 12px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: ${theme.textMuted}; margin-bottom: 8px; }
  .form-input {
    width: 100%; padding: 14px 16px;
    background: ${theme.bgInput};
    border: 1px solid ${theme.border};
    border-radius: 10px;
    color: ${theme.text}; font-family: 'Rajdhani', sans-serif; font-size: 15px;
    outline: none; transition: all 0.2s;
  }
  .form-input:focus { border-color: ${theme.primary}; box-shadow: 0 0 0 3px rgba(192,21,42,0.15); }
  .form-input::placeholder { color: ${theme.textMuted}; }

  .btn-primary {
    width: 100%; padding: 15px;
    background: ${theme.gradHigh};
    border: none; border-radius: 10px;
    color: white; font-family: 'Rajdhani', sans-serif; font-size: 16px; font-weight: 700;
    letter-spacing: 2px; text-transform: uppercase;
    cursor: pointer; transition: all 0.2s;
    box-shadow: 0 4px 20px rgba(192,21,42,0.4);
  }
  .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(192,21,42,0.55); }
  .btn-primary:active { transform: translateY(0); }

  .auth-footer { text-align: center; margin-top: 24px; color: ${theme.textMuted}; font-size: 14px; }
  .auth-link { color: ${theme.accent}; cursor: pointer; font-weight: 600; text-decoration: none; }
  .auth-link:hover { color: ${theme.primaryLight}; text-decoration: underline; }

  .divider { border: none; border-top: 1px solid ${theme.border}; margin: 20px 0; }

  /* NAVBAR */
  .navbar {
    display: flex; align-items: center; justify-content: space-between;
    padding: 16px 24px;
    background: rgba(13,3,5,0.9);
    border-bottom: 1px solid ${theme.border};
    backdrop-filter: blur(10px);
    position: sticky; top: 0; z-index: 100;
  }
  .nav-brand { font-family: 'Bebas Neue', sans-serif; font-size: 26px; letter-spacing: 3px; color: ${theme.accent}; }
  .nav-actions { display: flex; gap: 12px; align-items: center; }
  .nav-btn {
    padding: 8px 18px;
    background: transparent; border: 1px solid ${theme.border};
    border-radius: 8px; color: ${theme.textMuted}; font-family: 'Rajdhani', sans-serif;
    font-size: 13px; font-weight: 600; letter-spacing: 1px;
    cursor: pointer; transition: all 0.2s;
  }
  .nav-btn:hover { border-color: ${theme.primary}; color: ${theme.text}; }
  .nav-btn.active { background: ${theme.primary}; border-color: ${theme.primary}; color: white; }

  /* HOME PAGE */
  .home-page { padding: 24px; }
  .market-header {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 24px;
  }
  .market-title { font-family: 'Bebas Neue', sans-serif; font-size: 28px; letter-spacing: 2px; }
  .live-badge {
    display: flex; align-items: center; gap: 6px;
    background: rgba(192,21,42,0.15); border: 1px solid ${theme.primary};
    border-radius: 20px; padding: 5px 14px;
    font-size: 12px; font-weight: 700; letter-spacing: 1.5px; color: ${theme.accent};
  }
  .live-dot { width: 7px; height: 7px; border-radius: 50%; background: ${theme.accent}; animation: blink 1s infinite; }
  @keyframes blink { 0%,100% { opacity:1; } 50% { opacity:0; } }

  /* PRICE CARD */
  .price-card {
    background: ${theme.bgCard};
    border: 1px solid ${theme.border};
    border-radius: 20px;
    padding: 28px;
    margin-bottom: 24px;
    position: relative; overflow: hidden;
    box-shadow: 0 8px 40px rgba(0,0,0,0.4);
    animation: slideUp 0.4s ease;
  }
  .price-card::before {
    content: '';
    position: absolute; top: -50px; right: -50px;
    width: 180px; height: 180px;
    background: radial-gradient(circle, rgba(192,21,42,0.12), transparent 70%);
    border-radius: 50%;
  }
  .price-card-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; }
  .asset-info { }
  .asset-name { font-family: 'Orbitron', sans-serif; font-size: 22px; font-weight: 700; }
  .asset-sub { color: ${theme.textMuted}; font-size: 13px; margin-top: 4px; }
  .asset-change {
    padding: 6px 14px; border-radius: 8px; font-weight: 700; font-size: 14px;
  }
  .change-up { background: rgba(46,204,113,0.15); color: #2ecc71; }
  .change-down { background: rgba(231,76,60,0.15); color: #e74c3c; }

  .price-main { margin-bottom: 20px; }
  .price-value { font-family: 'Bebas Neue', sans-serif; font-size: 56px; line-height:1; color: white; letter-spacing: 2px; }
  .price-unit { font-size: 18px; color: ${theme.textMuted}; margin-top: 4px; }

  .price-stats { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; }
  .stat-item { text-align: center; }
  .stat-label { font-size: 11px; color: ${theme.textMuted}; letter-spacing: 1px; text-transform: uppercase; }
  .stat-value { font-family: 'Orbitron', sans-serif; font-size: 16px; font-weight: 700; margin-top: 4px; }
  .stat-value.up { color: #2ecc71; }
  .stat-value.down { color: #e74c3c; }

  /* BID BUTTONS */
  .bid-section { margin-bottom: 24px; }
  .bid-section-title { font-size: 13px; color: ${theme.textMuted}; letter-spacing: 1.5px; text-transform: uppercase; margin-bottom: 16px; font-weight: 600; }
  .bid-buttons { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }

  .bid-btn {
    padding: 22px 20px;
    border: none; border-radius: 16px;
    cursor: pointer; transition: all 0.25s;
    display: flex; flex-direction: column; align-items: center; gap: 6px;
    position: relative; overflow: hidden;
  }
  .bid-btn::after {
    content: '';
    position: absolute; inset: 0;
    background: white; opacity: 0;
    transition: opacity 0.2s;
  }
  .bid-btn:active::after { opacity: 0.07; }
  .bid-btn-label { font-size: 11px; letter-spacing: 2px; font-weight: 600; text-transform: uppercase; opacity: 0.8; }
  .bid-btn-icon { font-size: 28px; }
  .bid-btn-text { font-family: 'Bebas Neue', sans-serif; font-size: 28px; letter-spacing: 2px; }

  .btn-high {
    background: linear-gradient(145deg, #1a6b35 0%, #0e4520 100%);
    border: 1px solid #2ecc71;
    color: #2ecc71;
    box-shadow: 0 6px 30px rgba(46,204,113,0.2);
  }
  .btn-high:hover { transform: translateY(-3px); box-shadow: 0 12px 40px rgba(46,204,113,0.35); }

  .btn-low {
    background: linear-gradient(145deg, #8b0e1e 0%, #5a0912 100%);
    border: 1px solid ${theme.primary};
    color: ${theme.accent};
    box-shadow: 0 6px 30px rgba(192,21,42,0.3);
  }
  .btn-low:hover { transform: translateY(-3px); box-shadow: 0 12px 40px rgba(192,21,42,0.5); }

  /* BID AMOUNT */
  .amount-section {
    background: ${theme.bgCard};
    border: 1px solid ${theme.border};
    border-radius: 16px;
    padding: 24px;
    margin-bottom: 24px;
    animation: slideUp 0.3s ease;
  }
  .amount-title { font-size: 13px; color: ${theme.textMuted}; letter-spacing: 1.5px; text-transform: uppercase; margin-bottom: 16px; font-weight: 600; }
  .amount-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 16px; }
  .amount-chip {
    padding: 12px 8px;
    background: ${theme.bgInput}; border: 1px solid ${theme.border};
    border-radius: 10px; text-align: center;
    cursor: pointer; font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 15px;
    transition: all 0.2s; color: ${theme.text};
  }
  .amount-chip:hover, .amount-chip.selected { background: ${theme.primary}; border-color: ${theme.primary}; color: white; }
  .amount-input-row { display: flex; gap: 12px; }
  .amount-input {
    flex: 1; padding: 14px 16px;
    background: ${theme.bgInput}; border: 1px solid ${theme.border};
    border-radius: 10px; color: ${theme.text}; font-family: 'Orbitron', sans-serif; font-size: 18px;
    outline: none; transition: all 0.2s;
  }
  .amount-input:focus { border-color: ${theme.primary}; box-shadow: 0 0 0 3px rgba(192,21,42,0.15); }
  .place-bid-btn {
    padding: 14px 24px;
    background: ${theme.gradHigh}; border: none; border-radius: 10px;
    color: white; font-family: 'Rajdhani', sans-serif; font-size: 15px; font-weight: 700;
    letter-spacing: 1.5px; text-transform: uppercase;
    cursor: pointer; transition: all 0.2s;
    box-shadow: 0 4px 20px rgba(192,21,42,0.4);
    white-space: nowrap;
  }
  .place-bid-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(192,21,42,0.55); }

  /* LAST BID RESULT */
  .bid-result {
    background: ${theme.bgCard};
    border: 1px solid ${theme.border};
    border-radius: 12px;
    padding: 16px 20px;
    margin-bottom: 20px;
    display: flex; align-items: center; gap: 14px;
    animation: slideUp 0.3s ease;
  }
  .bid-result-icon { font-size: 26px; }
  .bid-result-info { flex: 1; }
  .bid-result-label { font-size: 12px; color: ${theme.textMuted}; letter-spacing: 1px; text-transform: uppercase; }
  .bid-result-val { font-family: 'Orbitron', sans-serif; font-size: 18px; font-weight: 700; margin-top: 2px; }

  /* COUNTDOWN */
  .countdown-page {
    min-height: 100vh;
    display: flex; align-items: center; justify-content: center;
    background: radial-gradient(ellipse at center, #1a0508 0%, #0d0305 70%);
    flex-direction: column; gap: 32px; padding: 20px;
  }
  .countdown-container { text-align: center; }
  .countdown-title { font-family: 'Bebas Neue', sans-serif; font-size: 28px; letter-spacing: 4px; color: ${theme.textMuted}; margin-bottom: 8px; }
  .countdown-bid-info { 
    font-family: 'Rajdhani', sans-serif; font-size: 16px; color: ${theme.textMuted};
    margin-bottom: 40px; letter-spacing: 1px;
  }
  .countdown-bid-info span { color: ${theme.accent}; font-weight: 700; }

  .countdown-ring {
    position: relative; width: 220px; height: 220px; margin: 0 auto 32px;
  }
  .countdown-ring svg { position: absolute; top:0; left:0; transform: rotate(-90deg); }
  .countdown-ring circle {
    transition: stroke-dashoffset 0.95s linear;
    stroke-linecap: round;
  }
  .countdown-number {
    position: absolute; inset: 0;
    display: flex; align-items: center; justify-content: center;
    font-family: 'Orbitron', sans-serif; font-size: 72px; font-weight: 900;
    color: white;
    animation: numberPop 0.3s ease;
  }
  @keyframes numberPop {
    0%   { transform: scale(1.5); opacity: 0.5; }
    100% { transform: scale(1); opacity: 1; }
  }

  .countdown-progress-labels {
    display: flex; justify-content: center; gap: 8px; flex-wrap: wrap;
  }
  .progress-dot {
    width: 32px; height: 32px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    font-family: 'Orbitron', sans-serif; font-size: 12px; font-weight: 700;
    transition: all 0.3s;
  }
  .progress-dot.done { background: ${theme.primary}; color: white; }
  .progress-dot.current { background: ${theme.accent}; color: white; box-shadow: 0 0 12px ${theme.accent}; transform: scale(1.1); }
  .progress-dot.pending { background: ${theme.bgCard}; border: 1px solid ${theme.border}; color: ${theme.textMuted}; }

  .countdown-actions { display: flex; gap: 16px; }
  .btn-cancel {
    padding: 14px 28px;
    background: transparent; border: 1px solid ${theme.border};
    border-radius: 10px; color: ${theme.textMuted}; font-family: 'Rajdhani', sans-serif;
    font-size: 15px; font-weight: 700; letter-spacing: 1.5px;
    cursor: pointer; transition: all 0.2s; text-transform: uppercase;
  }
  .btn-cancel:hover { border-color: ${theme.primary}; color: ${theme.text}; }

  /* HISTORY PAGE */
  .history-page { padding: 24px; }
  .history-header { margin-bottom: 24px; }
  .history-header h2 { font-family: 'Bebas Neue', sans-serif; font-size: 30px; letter-spacing: 3px; }
  .history-header p { color: ${theme.textMuted}; font-size: 14px; margin-top: 4px; }

  .history-filters { display: flex; gap: 10px; margin-bottom: 20px; flex-wrap: wrap; }
  .filter-btn {
    padding: 8px 18px;
    background: ${theme.bgCard}; border: 1px solid ${theme.border};
    border-radius: 20px; cursor: pointer;
    font-family: 'Rajdhani', sans-serif; font-size: 13px; font-weight: 600; letter-spacing: 1px;
    color: ${theme.textMuted}; transition: all 0.2s;
  }
  .filter-btn.active { background: ${theme.primary}; border-color: ${theme.primary}; color: white; }

  .history-list { display: flex; flex-direction: column; gap: 12px; }
  .history-item {
    background: ${theme.bgCard};
    border: 1px solid ${theme.border};
    border-radius: 14px; padding: 18px 20px;
    display: flex; align-items: center; gap: 16px;
    animation: slideUp 0.3s ease;
    transition: border-color 0.2s;
  }
  .history-item:hover { border-color: ${theme.primaryDark}; }
  .history-item-icon {
    width: 44px; height: 44px; border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    font-size: 20px; flex-shrink: 0;
  }
  .icon-high { background: rgba(46,204,113,0.1); }
  .icon-low { background: rgba(192,21,42,0.15); }
  .history-item-info { flex: 1; }
  .history-item-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; }
  .history-item-asset { font-family: 'Orbitron', sans-serif; font-size: 14px; font-weight: 700; }
  .history-item-amount { font-family: 'Orbitron', sans-serif; font-size: 16px; font-weight: 700; }
  .history-item-bottom { display: flex; justify-content: space-between; align-items: center; }
  .history-item-type {
    font-size: 12px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase;
    padding: 3px 10px; border-radius: 4px;
  }
  .type-high { background: rgba(46,204,113,0.1); color: #2ecc71; }
  .type-low { background: rgba(192,21,42,0.1); color: ${theme.accent}; }
  .history-item-time { font-size: 12px; color: ${theme.textMuted}; }
  .history-item-status {
    font-size: 12px; font-weight: 600; letter-spacing: 1px;
  }
  .status-won { color: #2ecc71; }
  .status-lost { color: ${theme.accent}; }
  .status-pending { color: ${theme.accentGold}; }

  .history-summary {
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px;
    margin-bottom: 24px;
  }
  .summary-card {
    background: ${theme.bgCard}; border: 1px solid ${theme.border}; border-radius: 14px;
    padding: 18px; text-align: center;
  }
  .summary-card-val { font-family: 'Orbitron', sans-serif; font-size: 22px; font-weight: 700; }
  .summary-card-label { font-size: 11px; color: ${theme.textMuted}; letter-spacing: 1px; text-transform: uppercase; margin-top: 6px; }

  /* FORGOT PASSWORD */
  .fp-steps { display: flex; gap: 8px; margin-bottom: 28px; justify-content: center; }
  .fp-step {
    display: flex; align-items: center; gap: 6px;
    font-size: 12px; font-weight: 600; letter-spacing: 1px; text-transform: uppercase;
  }
  .fp-step-num {
    width: 24px; height: 24px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 12px; font-weight: 700;
  }
  .fp-step-num.active { background: ${theme.primary}; color: white; }
  .fp-step-num.done { background: #2ecc71; color: white; }
  .fp-step-num.inactive { background: ${theme.border}; color: ${theme.textMuted}; }
  .fp-step-label { color: ${theme.textMuted}; }
  .fp-step-label.active { color: ${theme.text}; }
  .fp-step-sep { color: ${theme.border}; margin: 0 4px; }

  .otp-row { display: flex; gap: 10px; justify-content: center; margin: 20px 0; }
  .otp-input {
    width: 50px; height: 58px;
    background: ${theme.bgInput}; border: 1px solid ${theme.border};
    border-radius: 10px; color: white; font-family: 'Orbitron', sans-serif;
    font-size: 22px; font-weight: 700; text-align: center;
    outline: none; transition: all 0.2s;
  }
  .otp-input:focus { border-color: ${theme.primary}; box-shadow: 0 0 0 3px rgba(192,21,42,0.15); }

  /* BOTTOM NAV */
  .bottom-nav {
    position: fixed; bottom: 0; left: 0; right: 0;
    background: rgba(13,3,5,0.95); border-top: 1px solid ${theme.border};
    display: flex; justify-content: space-around; padding: 10px 0 14px;
    backdrop-filter: blur(10px); z-index: 100;
  }
  .bottom-nav-item {
    display: flex; flex-direction: column; align-items: center; gap: 4px;
    cursor: pointer; transition: all 0.2s; padding: 4px 16px; border-radius: 10px;
    background: none; border: none;
  }
  .bottom-nav-item.active .nav-icon { color: ${theme.accent}; }
  .bottom-nav-item.active .nav-label { color: ${theme.accent}; }
  .nav-icon { font-size: 22px; color: ${theme.textMuted}; transition: all 0.2s; }
  .nav-label { font-size: 11px; color: ${theme.textMuted}; letter-spacing: 1px; font-weight: 600; transition: all 0.2s; }

  .page-content { padding-bottom: 80px; }

  /* CONFETTI */
  @keyframes confetti-fall {
    0%   { transform: translateY(-20px) rotate(0deg); opacity: 1; }
    100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
  }
  .confetti { position: fixed; top:0; left:0; width:100%; height:100%; pointer-events:none; z-index:999; }
  .confetti-piece {
    position: absolute; width: 8px; height: 8px; border-radius: 2px;
    animation: confetti-fall linear forwards;
  }

  /* TOAST */
  .toast {
    position: fixed; bottom: 90px; left: 50%; transform: translateX(-50%);
    background: ${theme.bgCard}; border: 1px solid ${theme.primary};
    border-radius: 12px; padding: 14px 24px;
    font-weight: 600; font-size: 14px; letter-spacing: 0.5px;
    z-index: 1000; animation: slideUp 0.3s ease;
    box-shadow: 0 4px 24px rgba(192,21,42,0.3);
    white-space: nowrap;
  }
`;

// ─── STATIC DATA ──────────────────────────────────────
const HISTORY_DATA = [
  {
    id: 1,
    asset: "BTC/USD",
    type: "HIGH",
    amount: "$500",
    time: "2 min ago",
    status: "won",
    icon: "₿",
  },
  {
    id: 2,
    asset: "ETH/USD",
    type: "LOW",
    amount: "$200",
    time: "15 min ago",
    status: "lost",
    icon: "Ξ",
  },
  {
    id: 3,
    asset: "GOLD",
    type: "HIGH",
    amount: "$1,000",
    time: "1 hr ago",
    status: "won",
    icon: "🥇",
  },
  {
    id: 4,
    asset: "OIL/WTI",
    type: "LOW",
    amount: "$750",
    time: "3 hr ago",
    status: "lost",
    icon: "🛢",
  },
  {
    id: 5,
    asset: "BTC/USD",
    type: "HIGH",
    amount: "$300",
    time: "5 hr ago",
    status: "won",
    icon: "₿",
  },
  {
    id: 6,
    asset: "EUR/USD",
    type: "LOW",
    amount: "$150",
    time: "Yesterday",
    status: "pending",
    icon: "€",
  },
  {
    id: 7,
    asset: "NASDAQ",
    type: "HIGH",
    amount: "$2,000",
    time: "Yesterday",
    status: "won",
    icon: "📈",
  },
  {
    id: 8,
    asset: "SILVER",
    type: "LOW",
    amount: "$400",
    time: "2 days ago",
    status: "lost",
    icon: "⚡",
  },
];

const PRICE_DATA = {
  name: "BTC/USD",
  sub: "Bitcoin · Crypto Market",
  price: "67,842.50",
  change: "+3.24%",
  isUp: true,
  high: "69,200.00",
  low: "65,450.00",
  vol: "42.8B",
};

const AMOUNTS = [
  "$50",
  "$100",
  "$250",
  "$500",
  "$1,000",
  "$2,500",
  "$5,000",
  "$10K",
];

// ─── COMPONENTS ───────────────────────────────────────

function Toast({ msg }) {
  return <div className="toast">✓ {msg}</div>;
}

function Confetti() {
  const pieces = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 1.5,
    duration: 1.5 + Math.random() * 1.5,
    color: ["#c0152a", "#ff4d5e", "#f5c518", "#2ecc71", "#e74c3c", "#ff6b7a"][
      i % 6
    ],
  }));
  return (
    <div className="confetti">
      {pieces.map((p) => (
        <div
          key={p.id}
          className="confetti-piece"
          style={{
            left: `${p.left}%`,
            background: p.color,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        />
      ))}
    </div>
  );
}

// ─── LOGIN ─────────────────────────────────────────────
function LoginPage({ setPage }) {
  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">
          <div className="logo-icon">🔥</div>
          <h1>BIDFIRE</h1>
          <p>Trade · Bet · Win</p>
        </div>
        <div className="form-group">
          <label className="form-label">Email Address</label>
          <input
            className="form-input"
            type="email"
            placeholder="you@example.com"
            defaultValue="demo@bidfire.com"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Password</label>
          <input
            className="form-input"
            type="password"
            placeholder="••••••••"
            defaultValue="password"
          />
        </div>
        <div style={{ textAlign: "right", marginBottom: "20px" }}>
          <span className="auth-link" onClick={() => setPage("forgot")}>
            Forgot Password?
          </span>
        </div>
        <button className="btn-primary" onClick={() => setPage("home")}>
          SIGN IN →
        </button>
        <hr className="divider" />
        <div className="auth-footer">
          Don't have an account?&nbsp;
          <span className="auth-link" onClick={() => setPage("register")}>
            Create Account
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── REGISTER ──────────────────────────────────────────
function RegisterPage({ setPage }) {
  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">
          <div className="logo-icon">🔥</div>
          <h1>JOIN BIDFIRE</h1>
          <p>Create your trading account</p>
        </div>
        {[
          "Full Name",
          "Email Address",
          "Phone Number",
          "Password",
          "Confirm Password",
        ].map((label, i) => (
          <div className="form-group" key={i}>
            <label className="form-label">{label}</label>
            <input
              className="form-input"
              type={label.toLowerCase().includes("pass") ? "password" : "text"}
              placeholder={
                label === "Full Name"
                  ? "John Doe"
                  : label === "Email Address"
                    ? "you@example.com"
                    : label === "Phone Number"
                      ? "+1 234 567 890"
                      : "••••••••"
              }
            />
          </div>
        ))}
        <button className="btn-primary" onClick={() => setPage("home")}>
          CREATE ACCOUNT →
        </button>
        <hr className="divider" />
        <div className="auth-footer">
          Already have an account?&nbsp;
          <span className="auth-link" onClick={() => setPage("login")}>
            Sign In
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── FORGOT PASSWORD ───────────────────────────────────
function ForgotPage({ setPage }) {
  const [step, setStep] = useState(1);

  const steps = ["Email", "OTP", "Reset"];

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">
          <div className="logo-icon">🔑</div>
          <h1>RECOVER</h1>
          <p>Reset your password</p>
        </div>

        <div className="fp-steps">
          {steps.map((s, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center" }}>
              <div className="fp-step">
                <div
                  className={`fp-step-num ${i + 1 < step ? "done" : i + 1 === step ? "active" : "inactive"}`}
                >
                  {i + 1 < step ? "✓" : i + 1}
                </div>
                <span
                  className={`fp-step-label ${i + 1 === step ? "active" : ""}`}
                >
                  {s}
                </span>
              </div>
              {i < steps.length - 1 && (
                <span className="fp-step-sep" style={{ marginLeft: 8 }}>
                  ›
                </span>
              )}
            </div>
          ))}
        </div>

        {step === 1 && (
          <>
            <p
              style={{
                color: theme.textMuted,
                fontSize: 14,
                marginBottom: 20,
                lineHeight: 1.6,
              }}
            >
              Enter your registered email. We'll send a verification code.
            </p>
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input
                className="form-input"
                type="email"
                placeholder="you@example.com"
              />
            </div>
            <button className="btn-primary" onClick={() => setStep(2)}>
              SEND OTP →
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <p
              style={{
                color: theme.textMuted,
                fontSize: 14,
                marginBottom: 4,
                lineHeight: 1.6,
              }}
            >
              Enter the 6-digit code sent to your email.
            </p>
            <div className="otp-row">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <input key={i} className="otp-input" maxLength={1} />
              ))}
            </div>
            <button className="btn-primary" onClick={() => setStep(3)}>
              VERIFY CODE →
            </button>
            <div className="auth-footer" style={{ marginTop: 16 }}>
              Didn't receive?&nbsp;<span className="auth-link">Resend OTP</span>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <p
              style={{
                color: theme.textMuted,
                fontSize: 14,
                marginBottom: 20,
                lineHeight: 1.6,
              }}
            >
              Set a new strong password for your account.
            </p>
            <div className="form-group">
              <label className="form-label">New Password</label>
              <input
                className="form-input"
                type="password"
                placeholder="••••••••"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <input
                className="form-input"
                type="password"
                placeholder="••••••••"
              />
            </div>
            <button className="btn-primary" onClick={() => setPage("login")}>
              RESET PASSWORD ✓
            </button>
          </>
        )}

        <div className="auth-footer" style={{ marginTop: 20 }}>
          <span className="auth-link" onClick={() => setPage("login")}>
            ← Back to Login
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── COUNTDOWN ─────────────────────────────────────────
const TOTAL = 10;
function CountdownPage({ bidInfo, setPage, onComplete }) {
  const [count, setCount] = useState(TOTAL);
  const [key, setKey] = useState(0);

  useEffect(() => {
    if (count <= 0) {
      onComplete();
      return;
    }
    const t = setTimeout(() => {
      setCount((c) => c - 1);
      setKey((k) => k + 1);
    }, 1000);
    return () => clearTimeout(t);
  }, [count]);

  const radius = 96;
  const circ = 2 * Math.PI * radius;
  const offset = circ * (1 - count / TOTAL);

  return (
    <div className="countdown-page">
      <div className="countdown-container">
        <div className="countdown-title">PLACING BID</div>
        <div className="countdown-bid-info">
          <span>{bidInfo.type === "high" ? "▲ HIGH" : "▼ LOW"}</span>
          &nbsp;·&nbsp;
          <span>{bidInfo.amount}</span>
          &nbsp;·&nbsp;
          <span>BTC/USD</span>
        </div>

        <div className="countdown-ring">
          <svg width="220" height="220" viewBox="0 0 220 220">
            <circle
              cx="110"
              cy="110"
              r={radius}
              fill="none"
              stroke={theme.border}
              strokeWidth="8"
            />
            <circle
              cx="110"
              cy="110"
              r={radius}
              fill="none"
              stroke={count > 5 ? theme.primary : theme.accent}
              strokeWidth="8"
              strokeDasharray={circ}
              strokeDashoffset={offset}
            />
          </svg>
          <div className="countdown-number" key={key}>
            {String(count).padStart(2, "0")}
          </div>
        </div>

        <div className="countdown-progress-labels">
          {Array.from({ length: TOTAL }, (_, i) => {
            const num = TOTAL - i;
            return (
              <div
                key={i}
                className={`progress-dot ${num > count ? "done" : num === count ? "current" : "pending"}`}
              >
                {String(num).padStart(2, "0")}
              </div>
            );
          })}
        </div>
      </div>

      <div className="countdown-actions">
        <button className="btn-cancel" onClick={() => setPage("home")}>
          CANCEL BID
        </button>
      </div>
    </div>
  );
}

// ─── HISTORY ───────────────────────────────────────────
function HistoryPage() {
  const [filter, setFilter] = useState("ALL");
  const filters = ["ALL", "HIGH", "LOW", "WON", "LOST"];
  const filtered = HISTORY_DATA.filter((h) => {
    if (filter === "ALL") return true;
    if (filter === "HIGH") return h.type === "HIGH";
    if (filter === "LOW") return h.type === "LOW";
    if (filter === "WON") return h.status === "won";
    if (filter === "LOST") return h.status === "lost";
    return true;
  });

  return (
    <div className="history-page page-content">
      <div className="history-header">
        <h2>BID HISTORY</h2>
        <p>Your recent trading activity</p>
      </div>

      <div className="history-summary">
        <div className="summary-card">
          <div className="summary-card-val" style={{ color: "#2ecc71" }}>
            12
          </div>
          <div className="summary-card-label">Won</div>
        </div>
        <div className="summary-card">
          <div className="summary-card-val" style={{ color: theme.accent }}>
            6
          </div>
          <div className="summary-card-label">Lost</div>
        </div>
        <div className="summary-card">
          <div className="summary-card-val" style={{ color: theme.accentGold }}>
            $4,750
          </div>
          <div className="summary-card-label">Net P&L</div>
        </div>
      </div>

      <div className="history-filters">
        {filters.map((f) => (
          <button
            key={f}
            className={`filter-btn ${filter === f ? "active" : ""}`}
            onClick={() => setFilter(f)}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="history-list">
        {filtered.map((item, idx) => (
          <div
            className="history-item"
            key={item.id}
            style={{ animationDelay: `${idx * 0.05}s` }}
          >
            <div
              className={`history-item-icon ${item.type === "HIGH" ? "icon-high" : "icon-low"}`}
            >
              {item.icon}
            </div>
            <div className="history-item-info">
              <div className="history-item-top">
                <span className="history-item-asset">{item.asset}</span>
                <span
                  className="history-item-amount"
                  style={{
                    color: item.type === "HIGH" ? "#2ecc71" : theme.accent,
                  }}
                >
                  {item.amount}
                </span>
              </div>
              <div className="history-item-bottom">
                <span
                  className={`history-item-type ${item.type === "HIGH" ? "type-high" : "type-low"}`}
                >
                  {item.type === "HIGH" ? "▲ HIGH" : "▼ LOW"}
                </span>
                <span className="history-item-time">{item.time}</span>
                <span className={`history-item-status status-${item.status}`}>
                  {item.status === "won"
                    ? "✓ WON"
                    : item.status === "lost"
                      ? "✗ LOST"
                      : "⏳ PENDING"}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── HOME ──────────────────────────────────────────────
function HomePage({ setPage, setBidInfo }) {
  const [selectedAmt, setSelectedAmt] = useState("$500");
  const [customAmt, setCustomAmt] = useState("");
  const [lastBid, setLastBid] = useState(null);
  const [toast, setToast] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);

  const finalAmt = customAmt || selectedAmt;

  const handleBid = (type) => {
    setBidInfo({ type, amount: finalAmt });
    setLastBid({ type, amount: finalAmt });
    setPage("countdown");
  };

  return (
    <div className="home-page page-content">
      {showConfetti && <Confetti />}
      {toast && <Toast msg={toast} />}

      <div className="market-header">
        <h2 className="market-title">LIVE MARKET</h2>
        <div className="live-badge">
          <div className="live-dot" /> LIVE
        </div>
      </div>

      {/* PRICE CARD */}
      <div className="price-card">
        <div className="price-card-header">
          <div className="asset-info">
            <div className="asset-name">{PRICE_DATA.name}</div>
            <div className="asset-sub">{PRICE_DATA.sub}</div>
          </div>
          <div
            className={`asset-change ${PRICE_DATA.isUp ? "change-up" : "change-down"}`}
          >
            {PRICE_DATA.change}
          </div>
        </div>

        <div className="price-main">
          <div className="price-value">${PRICE_DATA.price}</div>
          <div className="price-unit">USD · Last traded price</div>
        </div>

        <div className="price-stats">
          <div className="stat-item">
            <div className="stat-label">24H High</div>
            <div className="stat-value up">${PRICE_DATA.high}</div>
          </div>
          <div className="stat-item">
            <div className="stat-label">24H Low</div>
            <div className="stat-value down">${PRICE_DATA.low}</div>
          </div>
          <div className="stat-item">
            <div className="stat-label">Volume</div>
            <div className="stat-value">{PRICE_DATA.vol}</div>
          </div>
        </div>
      </div>

      {/* AMOUNT SELECTION */}
      <div className="amount-section">
        <div className="amount-title">Select Bid Amount</div>
        <div className="amount-grid">
          {AMOUNTS.map((a) => (
            <div
              key={a}
              className={`amount-chip ${selectedAmt === a && !customAmt ? "selected" : ""}`}
              onClick={() => {
                setSelectedAmt(a);
                setCustomAmt("");
              }}
            >
              {a}
            </div>
          ))}
        </div>
        <div className="amount-input-row">
          <input
            className="amount-input"
            placeholder="Custom..."
            value={customAmt}
            onChange={(e) => setCustomAmt(e.target.value)}
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              padding: "0 12px",
              background: theme.bgCard,
              borderRadius: 10,
              border: `1px solid ${theme.border}`,
              fontFamily: "Orbitron,sans-serif",
              fontSize: 20,
              fontWeight: 700,
              color: theme.accent,
            }}
          >
            {finalAmt}
          </div>
        </div>
      </div>

      {/* BID BUTTONS */}
      <div className="bid-section">
        <div className="bid-section-title">Place Your Bid</div>
        <div className="bid-buttons">
          <button
            className="bid-btn btn-high"
            onClick={() => handleBid("high")}
          >
            <span className="bid-btn-label">Predict</span>
            <span className="bid-btn-icon">▲</span>
            <span className="bid-btn-text">HIGH</span>
            <span className="bid-btn-label">{finalAmt}</span>
          </button>
          <button className="bid-btn btn-low" onClick={() => handleBid("low")}>
            <span className="bid-btn-label">Predict</span>
            <span className="bid-btn-icon">▼</span>
            <span className="bid-btn-text">LOW</span>
            <span className="bid-btn-label">{finalAmt}</span>
          </button>
        </div>
      </div>

      {/* LAST BID */}
      {lastBid && (
        <div
          className="bid-result"
          style={{
            borderColor: lastBid.type === "high" ? "#2ecc71" : theme.primary,
          }}
        >
          <div className="bid-result-icon">
            {lastBid.type === "high" ? "▲" : "▼"}
          </div>
          <div className="bid-result-info">
            <div className="bid-result-label">Last Bid Placed</div>
            <div
              className="bid-result-val"
              style={{
                color: lastBid.type === "high" ? "#2ecc71" : theme.accent,
              }}
            >
              {lastBid.type.toUpperCase()} · {lastBid.amount}
            </div>
          </div>
          <div style={{ fontSize: 12, color: theme.textMuted }}>Pending ⏳</div>
        </div>
      )}
    </div>
  );
}

// ─── MAIN APP ──────────────────────────────────────────
export default function App() {
  const [page, setPage] = useState("login");
  const [bidInfo, setBidInfo] = useState({ type: "high", amount: "$500" });
  const [showConfetti, setShowConfetti] = useState(false);

  const handleCountdownComplete = () => {
    setShowConfetti(true);
    setTimeout(() => {
      setShowConfetti(false);
      setPage("home");
    }, 3000);
  };

  const isAuthPage = ["login", "register", "forgot", "countdown"].includes(
    page,
  );

  return (
    <>
      <style>{STYLES}</style>
      <div className="app">
        {showConfetti && <Confetti />}

        {page === "login" && <LoginPage setPage={setPage} />}
        {page === "register" && <RegisterPage setPage={setPage} />}
        {page === "forgot" && <ForgotPage setPage={setPage} />}

        {page === "countdown" && (
          <CountdownPage
            bidInfo={bidInfo}
            setPage={setPage}
            onComplete={handleCountdownComplete}
          />
        )}

        {["home", "history"].includes(page) && (
          <>
            <nav className="navbar">
              <div className="nav-brand">🔥 BIDFIRE</div>
              <div className="nav-actions">
                <div
                  style={{
                    fontFamily: "Orbitron,sans-serif",
                    fontSize: 14,
                    color: theme.accentGold,
                  }}
                >
                  $12,450
                </div>
                <button className="nav-btn" onClick={() => setPage("login")}>
                  Logout
                </button>
              </div>
            </nav>

            {page === "home" && (
              <HomePage setPage={setPage} setBidInfo={setBidInfo} />
            )}
            {page === "history" && <HistoryPage />}

            <nav className="bottom-nav">
              {[
                { id: "home", icon: "🏠", label: "HOME" },
                { id: "history", icon: "📊", label: "HISTORY" },
              ].map((n) => (
                <button
                  key={n.id}
                  className={`bottom-nav-item ${page === n.id ? "active" : ""}`}
                  onClick={() => setPage(n.id)}
                >
                  <span className="nav-icon">{n.icon}</span>
                  <span className="nav-label">{n.label}</span>
                </button>
              ))}
            </nav>
          </>
        )}
      </div>
    </>
  );
}
